export const URL_REGEX = /^(https?:\/\/)?([\w-]+\.)+[\w-]+(\/[\w\-./?%&=]*)?$/i;
export const YT_REGEX = /(youtube\.com\/(watch|shorts|playlist)|youtu\.be\/)/i;

export const isLikelyUrl = (s) => {
  if (!s) return false;
  const trimmed = s.trim();
  if (!trimmed.includes(' ') && (trimmed.startsWith('http://') || trimmed.startsWith('https://'))) {
    return true;
  }
  return URL_REGEX.test(trimmed) && !trimmed.includes(' ');
};

export const isYoutube = (s) => YT_REGEX.test(s || '');

/**
 * Opaque pastel tint of `hex` blended with the card surface — NOT an alpha
 * color. A translucent background (e.g. `${hex}14`) lets whatever sits behind
 * the element (a modal backdrop, page content) show through; this returns a
 * solid rgb() so the element stays fully opaque. In dark mode it blends into
 * the dark surface (a bit stronger, so the tint stays visible).
 */
export const tintColor = (hex, dark = false) => {
  const base = dark ? [30, 30, 31] : [255, 255, 255];
  const ratio = dark ? 0.16 : 0.08;
  const h = (hex || '').replace('#', '');
  if (!/^[0-9a-fA-F]{6}$/.test(h)) return `rgb(${base.join(', ')})`;
  const rgb = [0, 2, 4].map((i) => parseInt(h.slice(i, i + 2), 16));
  const [r, g, b] = rgb.map((c, i) => Math.round(c * ratio + base[i] * (1 - ratio)));
  return `rgb(${r}, ${g}, ${b})`;
};

export const normalizeUrl = (u) => (u.startsWith('http') ? u : `https://${u}`);

/** Find every URL-looking whitespace-separated token in free text. */
export const extractUrls = (s) => (s ? (s.match(/\S+/g) || []).filter(isLikelyUrl) : []);

/** Extract a YouTube video ID from any canonical/shorts/short-link URL. */
export const youtubeId = (u) => {
  if (!u) return '';
  try {
    const url = new URL(u.startsWith('http') ? u : `https://${u}`);
    const host = url.hostname.replace(/^www\./, '');
    if (host === 'youtu.be') return url.pathname.slice(1).split('/')[0] || '';
    if (host.endsWith('youtube.com')) {
      if (url.pathname === '/watch') return url.searchParams.get('v') || '';
      const m = url.pathname.match(/^\/(shorts|embed|live)\/([\w-]+)/);
      if (m) return m[2];
    }
  } catch {}
  return '';
};

/** Best-effort thumbnail URL for a YouTube link (no API call). */
export const youtubeThumb = (u) => {
  const id = youtubeId(u);
  return id ? `https://img.youtube.com/vi/${id}/hqdefault.jpg` : '';
};

export const formatTime = (iso) => {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false });
  } catch {
    return '';
  }
};

export const domainOf = (u) => {
  try {
    return new URL(u.startsWith('http') ? u : `https://${u}`).hostname.replace(/^www\./, '');
  } catch {
    return '';
  }
};

export const truncate = (s, n) => (s && s.length > n ? s.slice(0, n - 1) + '…' : s || '');

/** Build a public, read-only deep-link URL for this note (no login required). */
export const buildNoteShareUrl = (doc) => {
  const url = new URL(window.location.origin);
  url.pathname = `/n/${encodeURIComponent(doc.user_id)}/${encodeURIComponent(doc.id)}`;
  return url.toString();
};

/** Share a note via the native share sheet, falling back to clipboard copy. */
export const shareNote = async (doc, t) => {
  const url = buildNoteShareUrl(doc);
  if (navigator.share) {
    try {
      await navigator.share({
        title: doc.title || t('share.noteTitle'),
        text: doc.title ? t('share.noteText', { title: doc.title }) : t('share.noteTextUntitled'),
        url,
      });
      return 'shared';
    } catch (e) {
      if (e.name === 'AbortError') return 'cancelled';
      // fall through to clipboard fallback
    }
  }
  await navigator.clipboard.writeText(url);
  return 'copied';
};

/** Build a public, read-only deep-link URL for a set of selected notes. */
export const buildCollectionShareUrl = (ownerId, ids) => {
  const url = new URL(window.location.origin);
  url.pathname = `/n/${encodeURIComponent(ownerId)}/collection`;
  url.searchParams.set('ids', ids.join(','));
  return url.toString();
};

/** Share multiple notes via the native share sheet, falling back to clipboard copy. */
export const shareCollection = async (ownerId, ids, t) => {
  const url = buildCollectionShareUrl(ownerId, ids);
  if (navigator.share) {
    try {
      await navigator.share({
        title: t('share.collectionTitle'),
        text: t('share.collectionText', { n: ids.length }),
        url,
      });
      return 'shared';
    } catch (e) {
      if (e.name === 'AbortError') return 'cancelled';
      // fall through to clipboard fallback
    }
  }
  await navigator.clipboard.writeText(url);
  return 'copied';
};

/** "5m ago" / "5 mnt lalu", falling back to a short date after a week.
 *  `t` + `language` come from useLanguage(). */
export const fmtRelative = (iso, t, language) => {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    const diff = (Date.now() - d.getTime()) / 1000;
    if (diff < 60) return t('time.justNow');
    if (diff < 3600) return t('time.minutesAgo', { n: Math.floor(diff / 60) });
    if (diff < 86400) return t('time.hoursAgo', { n: Math.floor(diff / 3600) });
    if (diff < 7 * 86400) return t('time.daysAgo', { n: Math.floor(diff / 86400) });
    return d.toLocaleDateString(language === 'id' ? 'id-ID' : 'en-US', { month: 'short', day: 'numeric' });
  } catch {
    return '';
  }
};
